import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import { createModal } from '@codegouvfr/react-dsfr/Modal';
import clsx from 'clsx';
import type { PartialAnalysis } from 'maestro-shared/schema/Analysis/Analysis';
import { type FunctionComponent, useContext, useMemo, useRef } from 'react';
import { Link } from 'react-router';
import { assert, type Equals } from 'tsafe';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import DocumentLink from '../../../../components/DocumentLink/DocumentLink';
import { ApiClientContext } from '../../../../services/apiClient';
import { getDocumentDownloadURL } from '../../../../services/document.service';
import { pluralize } from '../../../../utils/stringUtils';
import { AnalysisDocumentModal } from './AnalysisDocumentModal';

type Props = {
  partialAnalysis: PartialAnalysis | undefined;
  sampleId: string;
  itemNumber: number;
  copyNumber: number;
  readonly: boolean;
};

const addFileModal = createModal({
  id: `add-file-modale-id`,
  isOpenedByDefault: false
});

export const AnalysisDocumentPreview: FunctionComponent<Props> = ({
  partialAnalysis,
  sampleId,
  itemNumber,
  copyNumber,
  readonly,
  ..._rest
}) => {
  assert<Equals<keyof typeof _rest, never>>();
  const { useGetAnalysisReportDocumentIdsQuery } = useContext(ApiClientContext);

  const { currentData: reportDocumentIds } =
    useGetAnalysisReportDocumentIdsQuery(
      { analysisId: partialAnalysis?.id ?? '' },
      { skip: !partialAnalysis?.id }
    );

  return (
    <div
      className={cx(
        'fr-grid-row',
        'fr-grid-row--gutters',
        'fr-grid-row--bottom'
      )}
    >
      <div className={clsx(cx('fr-col-6'))}>
        <div className={cx('fr-mb-1v')}>
          {pluralize(reportDocumentIds?.length ?? 0)("Rapport d'analyse")}
          {reportDocumentIds?.length ? ` (${reportDocumentIds.length})` : ''}
        </div>
        {reportDocumentIds?.length ? (
          <Link
            to={getDocumentDownloadURL(reportDocumentIds[0], {
              type: 'sample',
              sampleId
            })}
            target="_blank"
            rel="noreferrer"
            className={cx('fr-link', 'fr-link--sm')}
          >
            Consulter le dernier rapport
          </Link>
        ) : (
          <span className={cx('fr-text--bold')}>Aucun rapport disponible</span>
        )}
      </div>
      <div className={clsx(cx('fr-col-6'))}>
        {!readonly && (
          <Button
            priority="tertiary"
            iconId="fr-icon-file-add-line"
            size="small"
            type={'button'}
            onClick={() => {
              addFileModal.open();
            }}
          >
            {reportDocumentIds?.length
              ? 'Actualiser le rapport'
              : 'Ajouter le rapport'}
          </Button>
        )}
        {partialAnalysis &&
          reportDocumentIds &&
          reportDocumentIds.length > 0 && (
            <ReportDocumentList
              readonly={readonly}
              sampleId={sampleId}
              analysisId={partialAnalysis.id}
              reportDocumentIds={reportDocumentIds}
            />
          )}
      </div>
      <AnalysisDocumentModal
        modal={addFileModal}
        sampleId={sampleId}
        itemNumber={itemNumber}
        copyNumber={copyNumber}
        partialAnalysis={partialAnalysis}
      />
    </div>
  );
};

const ReportDocumentList = ({
  readonly,
  sampleId,
  analysisId,
  reportDocumentIds
}: Pick<Props, 'readonly' | 'sampleId'> & {
  analysisId: string;
  reportDocumentIds: string[];
}) => {
  const { useDeleteAnalysisReportDocumentMutation } =
    useContext(ApiClientContext);

  const [deleteDocument] = useDeleteAnalysisReportDocumentMutation();

  const documentIdToDelete = useRef<null | string>(null);

  const onDeleteDocument = async () => {
    if (documentIdToDelete.current) {
      await deleteDocument({
        analysisId,
        documentId: documentIdToDelete.current
      });
      documentIdToDelete.current = null;
    }
  };
  const deleteReportDocumentConfirmationModal = useMemo(
    () =>
      createModal({
        id: `confirm-delete-report-document-${analysisId}`,
        isOpenedByDefault: false
      }),
    [analysisId]
  );

  const historyModal = useMemo(
    () =>
      createModal({
        id: `history-report-document-${analysisId}`,
        isOpenedByDefault: false
      }),
    [analysisId]
  );

  return (
    <>
      {reportDocumentIds.length > 1 && (
        <>
          <Button
            className={cx('fr-ml-1w')}
            priority="tertiary"
            iconId="fr-icon-time-line"
            size="small"
            type="button"
            onClick={() => {
              historyModal.open();
            }}
          >
            Historique
          </Button>
          <historyModal.Component
            title="Historique des rapports d'analyse"
            size="large"
          >
            <div
              className={'d-flex-align-start'}
              style={{ flexDirection: 'column', gap: '1rem' }}
            >
              {reportDocumentIds.map((id) => (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '1.5rem',
                    width: '100%'
                  }}
                  key={id}
                >
                  <span>
                    <DocumentLink
                      documentId={id}
                      scope={{ type: 'sample', sampleId }}
                    />
                  </span>
                  {!readonly && (
                    <Button
                      className={clsx(cx('fr-ml-auto'))}
                      iconId="fr-icon-delete-line"
                      size={'small'}
                      priority={'tertiary'}
                      title={'Supprimer'}
                      onClick={() => {
                        documentIdToDelete.current = id;
                        deleteReportDocumentConfirmationModal.open();
                      }}
                    ></Button>
                  )}
                </span>
              ))}
            </div>
          </historyModal.Component>
          <ConfirmationModal
            modal={deleteReportDocumentConfirmationModal}
            title="Confirmez la suppression du rapport d'analyse"
            onConfirm={onDeleteDocument}
            closeOnConfirm
          >
            Attention le rapport d'analyse sera supprimé définitivement.
          </ConfirmationModal>
        </>
      )}
    </>
  );
};
